/**
 * SubscriptionReturn — Phase 14
 *
 * Page /abonnement/retour appelée par Stripe après un checkout managed sandbox.
 * Rôle : polling de /api/stripe/session/{session_id} jusqu'à ce que le statut
 * soit résolu (paid | failed | expired) puis affichage clair du résultat.
 *
 * Aucune activation n'est déclenchée ici — la vérité est côté backend :
 *   - webhook (préféré) OU
 *   - fallback via retrieve() dans /api/stripe/session/{id}
 * Le frontend affiche ce que le backend renvoie, point final.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 30; // ≈ 60 seconds
const TERMINAL_STATUSES = new Set(["paid", "failed", "expired", "refunded"]);

function planLabel(plan) {
    if (plan === "monthly") return "Mensuel · 24 €/mois";
    if (plan === "annual") return "Annuel · 240 €/an";
    return plan ? plan : "—";
}

export default function SubscriptionReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState(sessionId ? "polling" : "missing_session");
    const [detail, setDetail] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const attemptsRef = useRef(0);
    const stoppedRef = useRef(false);

    useEffect(() => {
        if (!sessionId) return;

        // StrictMode-safe: reset the guard on every mount. React 18's
        // StrictMode double-invokes effects, and the first cleanup sets
        // `stoppedRef.current = true` — without this reset, the polling loop
        // would be permanently disabled on the second mount and the user
        // would hang on the spinner forever.
        stoppedRef.current = false;
        attemptsRef.current = 0;
        setAttempts(0);

        let timeoutId;

        async function pollOnce() {
            if (stoppedRef.current) return;
            attemptsRef.current += 1;
            setAttempts(attemptsRef.current);
            try {
                const { data } = await api.get(`/stripe/session/${sessionId}`);
                setDetail(data);
                if (TERMINAL_STATUSES.has(data.payment_status)) {
                    stoppedRef.current = true;
                    setStatus(data.payment_status === "paid" ? "success" : data.payment_status);
                    return;
                }
                if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
                    stoppedRef.current = true;
                    setStatus("timeout");
                    return;
                }
                timeoutId = setTimeout(pollOnce, POLL_INTERVAL_MS);
            } catch (err) {
                // 404/403 are terminal — the session either doesn't exist or
                // doesn't belong to this user. No point retrying.
                const code = err?.response?.status;
                if (code === 404 || code === 403) {
                    stoppedRef.current = true;
                    setStatus("not_found");
                    return;
                }
                console.warn("[SubscriptionReturn] polling failed:", err);
                if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
                    stoppedRef.current = true;
                    setStatus("timeout");
                    return;
                }
                timeoutId = setTimeout(pollOnce, POLL_INTERVAL_MS);
            }
        }

        pollOnce();

        return () => {
            stoppedRef.current = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-paper" data-testid="page-subscription-return">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                <p className="text-[11px] tracking-[0.28em] uppercase text-inkSoft mb-3">
                    Abonnement · retour paiement
                </p>

                {status === "missing_session" && (
                    <ErrorPanel
                        title="Session manquante"
                        message="Aucun identifiant de session Stripe n'a été fourni. Retournez à la page d'abonnement pour relancer un paiement."
                    />
                )}

                {status === "polling" && (
                    <div className="rounded-3xl border border-lexiBorder bg-white p-8 md:p-10" data-testid="return-polling">
                        <div className="flex items-center gap-3 mb-4">
                            <Loader2 className="w-5 h-5 text-terracotta animate-spin" />
                            <h1 className="font-serif text-2xl text-ink">
                                Vérification de votre paiement…
                            </h1>
                        </div>
                        <p className="text-sm text-inkSoft mb-2">
                            Stripe nous confirme l'état du paiement. Merci de patienter quelques secondes.
                        </p>
                        <p className="text-xs text-inkSoft opacity-70">
                            Tentative {attempts} / {POLL_MAX_ATTEMPTS}
                        </p>
                    </div>
                )}

                {status === "success" && (
                    <SuccessPanel plan={detail?.plan} />
                )}

                {status === "failed" && (
                    <ErrorPanel
                        title="Paiement refusé"
                        message="Votre paiement n'a pas abouti. Aucun abonnement n'a été activé. Vous pouvez réessayer depuis la page d'abonnement."
                    />
                )}

                {status === "expired" && (
                    <ErrorPanel
                        title="Session expirée"
                        message="La session de paiement a expiré. Aucun abonnement n'a été activé. Vous pouvez recommencer un paiement depuis la page d'abonnement."
                    />
                )}

                {status === "refunded" && (
                    <ErrorPanel
                        title="Paiement remboursé"
                        message="Ce paiement a été remboursé. L'abonnement associé a été désactivé."
                    />
                )}

                {status === "not_found" && (
                    <ErrorPanel
                        title="Session introuvable"
                        message="Cet identifiant de session Stripe n'est pas reconnu. Il a peut-être expiré, ou il ne correspond pas à votre compte. Retournez à la page d'abonnement pour relancer un paiement."
                    />
                )}

                {status === "timeout" && (
                    <div className="rounded-3xl border border-lexiBorder bg-white p-8 md:p-10" data-testid="return-timeout">
                        <div className="flex items-center gap-3 mb-4">
                            <Clock className="w-6 h-6 text-inkSoft" />
                            <h1 className="font-serif text-2xl text-ink">
                                Confirmation en cours
                            </h1>
                        </div>
                        <p className="text-sm text-inkSoft leading-relaxed">
                            Nous n'avons pas encore reçu la confirmation finale de Stripe.
                            Cela peut arriver lorsque le paiement est en traitement asynchrone (virement, wallet). Votre abonnement sera activé automatiquement dès la confirmation.
                        </p>
                        <p className="text-sm text-inkSoft mt-3">
                            Vous pouvez actualiser cette page dans quelques instants, ou revenir sur votre abonnement.
                        </p>
                        <BackToAbonnement />
                    </div>
                )}
            </div>
        </div>
    );
}

function SuccessPanel({ plan }) {
    return (
        <div className="rounded-3xl border-2 border-emerald bg-white p-8 md:p-10" data-testid="return-success">
            <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald" strokeWidth={2} />
                <h1 className="font-serif text-3xl text-ink">
                    Abonnement actif
                </h1>
            </div>
            <p className="text-base text-ink mb-2">
                Bienvenue à bord ! Votre plan <strong data-testid="return-plan">{planLabel(plan)}</strong> est actif.
            </p>
            <p className="text-sm text-inkSoft mb-6 leading-relaxed">
                Tous les modules de Lexia School sont désormais débloqués : leçons quotidiennes, révisions, conversation IA, coach vocale, podcasts, parcours pros, certificats. Bonne route.
            </p>
            <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-ink hover:bg-emerald text-white rounded-full px-5 py-3 text-sm font-medium transition-colors"
                data-testid="return-cta-dashboard"
            >
                Aller au tableau de bord
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
        </div>
    );
}

function ErrorPanel({ title, message }) {
    return (
        <div className="rounded-3xl border border-lexiBorder bg-white p-8 md:p-10" data-testid="return-error">
            <div className="flex items-center gap-3 mb-4">
                <XCircle className="w-7 h-7 text-terracotta" strokeWidth={2} />
                <h1 className="font-serif text-2xl text-ink">{title}</h1>
            </div>
            <p className="text-sm text-inkSoft leading-relaxed">{message}</p>
            <BackToAbonnement />
        </div>
    );
}

function BackToAbonnement() {
    return (
        <Link
            to="/abonnement"
            className="mt-6 inline-flex items-center gap-2 bg-white border border-lexiBorder hover:bg-paperAlt text-ink rounded-full px-5 py-3 text-sm font-medium transition-colors"
            data-testid="return-back-cta"
        >
            Retour à l'abonnement
        </Link>
    );
}
